import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";

import type { IDeviceDistributionChartData } from "app/pages/industry-analysis/overview/widgets/device-distribution/types";
import { getAvailableWebSource } from "app/components/filters-bar/utils";
import type { WebSource } from "app/components/filters-bar/websource-filter/WebSourceFilter.types";
import { i18nFilter } from "app/filters/i18nFilter";
import { IndustryOverviewLoader } from "app/pages/industry-analysis/overview/components/common/IndustryOverviewLoader/IndustryOverviewLoader";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DeviceDistributionChartNoData } from "../../../../components/DeviceDistributionChartNoData/DeviceDistributionChartNoData";
import { adaptSingleMetricDataValue } from "../../config/chartConfigUtils";
import { IconWrapper, PercentageText, TextsContainer, TrafficText } from "../../DeviceDistributionSingleChart.styles";

interface IChartBodyProps {
    isLoading: boolean;
    data: IDeviceDistributionChartData;
    webSource: WebSource;
}

export const ChartBody: FC<IChartBodyProps> = (props) => {
    const { isLoading, data, webSource } = props;
    const { params } = useCurrentStateAndParams();
    const translate = i18nFilter();
    const iconName = useMemo(() => {
        return webSource === "Desktop" ? "desktop" : "mobile-web";
    }, [webSource]);

    const singleMetricValue = useMemo(() => {
        return data ? adaptSingleMetricDataValue(data, webSource) : null;
    }, [data, webSource]);

    if (isLoading) {
        return <IndustryOverviewLoader />;
    }

    if (!data) {
        return <DeviceDistributionChartNoData />;
    }

    // In the case of partial data (only a single webSource is available for the current country),
    // We want to show a message explaining that this is the only available webSource.
    const availableWebSources = getAvailableWebSource(proNavigator.current(), params);
    const hasSingleSource = availableWebSources.length === 1;
    const title = hasSingleSource ? webSource : `${singleMetricValue.toFixed(2)}%`;
    const subtitle = hasSingleSource ? translate("wa.ao.pie.dataonly") : translate("all.traffic");

    return (
        <>
            <IconWrapper>
                <SWReactIcons
                    iconName={iconName}
                    size={"xxl"}
                />
            </IconWrapper>
            <TextsContainer>
                <PercentageText>{title}</PercentageText>
                <TrafficText>{subtitle}</TrafficText>
            </TextsContainer>
        </>
    );
};
